import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { TopicMetadata } from '../types';
import {
    createInitialReadingProgress,
    readReadingProgress,
    writeReadingProgress,
    type ReadingProgressRecord,
} from '../utils/readingProgress';

const AUTO_COMPLETE_SCROLL_THRESHOLD_PERCENT = 90;
const AUTO_COMPLETE_MIN_DWELL_RATIO = 0.4;
const SCROLL_SAVE_DEBOUNCE_MS = 500;
const RESUME_SCROLL_MIN_PERCENT = 2;

interface UseReadingProgressResult {
    contentRef: RefObject<HTMLDivElement>;
    scrollPercent: number;
    completed: boolean;
    toggleCompleted: () => void;
}

function calculateScrollPercent(contentEl: HTMLDivElement): number {
    const rect = contentEl.getBoundingClientRect();
    if (rect.height <= 0) {
        return 0;
    }

    const contentTop = rect.top + window.scrollY;
    const viewportBottom = window.scrollY + window.innerHeight;
    const raw = ((viewportBottom - contentTop) / rect.height) * 100;

    return Math.min(100, Math.max(0, raw));
}

// Assumes the caller remounts this hook's owner when topic.slug changes
// (e.g. key={topic.slug}) - useState/useRef do not reset on prop change alone,
// and effects below deliberately re-derive fresh state per slug as a second guard.
export function useReadingProgress(topic: TopicMetadata): UseReadingProgressResult {
    const contentRef = useRef<HTMLDivElement>(null);
    const [progress, setProgress] = useState<ReadingProgressRecord>(
        () => readReadingProgress(topic.slug) ?? createInitialReadingProgress(),
    );

    // Updated synchronously at the same instant every new record is computed below,
    // never from the `progress` state itself - a ref synced only on render can still
    // be stale if a component unmounts before React commits a pending state update.
    const progressRef = useRef(progress);

    const manualOverrideRef = useRef(false);
    const mountTimeRef = useRef(0);
    const writeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        mountTimeRef.current = performance.now();
        manualOverrideRef.current = false;

        const fresh = readReadingProgress(topic.slug) ?? createInitialReadingProgress();
        const visited: ReadingProgressRecord = {
            ...fresh,
            visitCount: fresh.visitCount + 1,
            lastReadAt: new Date().toISOString(),
        };

        progressRef.current = visited;
        setProgress(visited);
        writeReadingProgress(topic.slug, visited);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic.slug]);

    useEffect(() => {
        const savedPercent = progressRef.current.scrollPercent;
        const frame = requestAnimationFrame(() => {
            const contentEl = contentRef.current;
            if (!contentEl || savedPercent <= RESUME_SCROLL_MIN_PERCENT) {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                return;
            }

            const rect = contentEl.getBoundingClientRect();
            const contentTop = rect.top + window.scrollY;
            const targetScrollY = contentTop + (savedPercent / 100) * rect.height - window.innerHeight;
            window.scrollTo({ top: Math.max(0, targetScrollY), left: 0, behavior: 'auto' });
        });

        return () => cancelAnimationFrame(frame);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic.slug]);

    useEffect(() => {
        const contentEl = contentRef.current;
        if (!contentEl) {
            return undefined;
        }

        let rafId: number | null = null;

        const handleScroll = () => {
            if (rafId !== null) {
                return;
            }

            rafId = requestAnimationFrame(() => {
                rafId = null;
                const percent = calculateScrollPercent(contentEl);
                const elapsedMs = performance.now() - mountTimeRef.current;
                const dwellThresholdMs = Math.max(topic.estimatedReadTime, 1) * 60_000 * AUTO_COMPLETE_MIN_DWELL_RATIO;

                setProgress((previous) => {
                    const shouldAutoComplete =
                        !manualOverrideRef.current &&
                        !previous.completed &&
                        percent >= AUTO_COMPLETE_SCROLL_THRESHOLD_PERCENT &&
                        elapsedMs >= dwellThresholdMs;

                    const next: ReadingProgressRecord = {
                        ...previous,
                        scrollPercent: percent,
                        completed: previous.completed || shouldAutoComplete,
                        completedAt: shouldAutoComplete ? new Date().toISOString() : previous.completedAt,
                    };

                    progressRef.current = next;
                    clearTimeout(writeTimeoutRef.current);
                    writeTimeoutRef.current = setTimeout(
                        () => writeReadingProgress(topic.slug, next),
                        SCROLL_SAVE_DEBOUNCE_MS,
                    );

                    return next;
                });
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            clearTimeout(writeTimeoutRef.current);
            writeReadingProgress(topic.slug, progressRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic.slug, topic.estimatedReadTime]);

    const toggleCompleted = useCallback(() => {
        manualOverrideRef.current = true;

        setProgress((previous) => {
            const completed = !previous.completed;
            const next: ReadingProgressRecord = {
                ...previous,
                completed,
                completedAt: completed ? new Date().toISOString() : null,
            };

            progressRef.current = next;
            writeReadingProgress(topic.slug, next);
            return next;
        });
    }, [topic.slug]);

    return {
        contentRef,
        scrollPercent: progress.scrollPercent,
        completed: progress.completed,
        toggleCompleted,
    };
}
